import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { alpha, styled } from '@mui/material/styles';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Checkbox } from '../Checkbox';
import { Typography } from '../Typography';
export interface ExpandableFilterSectionItem {
  uid: string;
  displayName: string;
  disabled?: boolean;
  count?: number;
}
export interface ExpandableFilterSectionProps {
  uid: string;
  title: string;
  items: ExpandableFilterSectionItem[];
  selectedUids: string[];
  onItemChange: (uid: string) => void;
  onSectionChange: (uids: string[], selectAll: boolean) => void;
  totalCount?: number;
  testIdPrefix?: string;
}
// Parent row with chevron - keyboard accessible
const ParentRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{
  isSelected?: boolean;
}>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 16px',
  borderRadius: '12px',
  cursor: 'pointer',
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : theme.custom.colours?.grey50,
  },
  // Focus styles for keyboard navigation
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));
// Child row - keyboard accessible
const ChildRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{
  isSelected?: boolean;
}>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 16px',
  borderRadius: '12px',
  cursor: 'pointer',
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : theme.custom.colours?.grey50,
  },
  // Focus styles for keyboard navigation
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));
// Container for child items with gap
const ChildrenContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginTop: '6px',
  paddingLeft: '52px',
}));
/**
 * Renders a filter section with expandable items matching the design system.
 * - Purple background/text when selected
 * - Chevron on left for parent, indentation for children
 * - Count and checkbox on right
 * - Always shows the group title, with items nested inside
 * - Full keyboard accessibility with ARIA attributes
 */
// Static color values from Figma design (not matching theme.custom.colours)
const COLORS = {
  textSecondary: '#414651', // Figma: text-secondary-(700), unselected text
  textTertiary: '#535862', // Figma: text-tertiary-(600), unselected count
  chevron: '#667085', // Figma: chevron color
};
export const ExpandableFilterSection: React.FC<ExpandableFilterSectionProps> = ({
  uid,
  title,
  items,
  selectedUids,
  onItemChange,
  onSectionChange,
  totalCount,
  testIdPrefix = 'filter-section',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectableItems = items.filter((item) => !item.disabled);
  const selectedCount = selectableItems.filter((item) => selectedUids.includes(item.uid)).length;
  const isAllSelected = selectableItems.length > 0 && selectedCount === selectableItems.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < selectableItems.length;
  const hasSelection = selectedCount > 0;
  const contentId = `${testIdPrefix}-${uid}-content`;
  const handleGroupCheckboxChange = () => {
    const uids = selectableItems.map((item) => item.uid);
    const shouldSelectAll = !isAllSelected && !isIndeterminate;
    onSectionChange(uids, shouldSelectAll);
  };
  const handleParentKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };
  const handleChildKeyDown = (event: React.KeyboardEvent, itemUid: string, disabled?: boolean) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
      event.preventDefault();
      onItemChange(itemUid);
    }
  };
  const handleGroupCheckboxKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleGroupCheckboxChange();
    }
  };
  const handleItemCheckboxKeyDown = (event: React.KeyboardEvent, itemUid: string, disabled?: boolean) => {
    if (event.key === 'Enter' && !disabled) {
      event.preventDefault();
      event.stopPropagation();
      onItemChange(itemUid);
    }
  };
  return (
    <Box data-testid={`${testIdPrefix}-${uid}`} role="group" aria-label={title}>
      {/* Parent row - always shows the group title */}
      <ParentRow
        isSelected={hasSelection}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={handleParentKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        {/* Chevron */}
        <Box
          sx={{
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-hidden="true"
        >
          {isExpanded ? (
            <ChevronDown size={20} color={COLORS.chevron} />
          ) : (
            <ChevronRight size={20} color={COLORS.chevron} />
          )}
        </Box>

        {/* Title - always use the group title */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: 500,
            color: hasSelection ? 'primary.main' : COLORS.textSecondary,
          }}
        >
          {title}
        </Typography>

        {/* Total Count */}
        {typeof totalCount === 'number' && (
          <Typography variant="body2" sx={{ color: hasSelection ? 'primary.main' : COLORS.textTertiary }}>
            {totalCount.toLocaleString()}
          </Typography>
        )}

        {/* Group Checkbox */}
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={handleGroupCheckboxKeyDown}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            disabled={selectableItems.length === 0}
            onChange={handleGroupCheckboxChange}
            size="small"
            slotProps={{
              input: {
                'aria-label': `Select all ${title}`,
              },
            }}
          />
        </Box>
      </ParentRow>

      {/* Child items - indented container as per Figma (52px left padding) */}
      <Collapse in={isExpanded}>
        <ChildrenContainer id={contentId} role="list">
          {items.map((item) => {
            const isSelected = selectedUids.includes(item.uid);
            return (
              <ChildRow
                key={item.uid}
                isSelected={isSelected}
                onClick={() => !item.disabled && onItemChange(item.uid)}
                onKeyDown={(e) => handleChildKeyDown(e, item.uid, item.disabled)}
                role="listitem"
                tabIndex={item.disabled ? -1 : 0}
                sx={{
                  cursor: item.disabled ? 'default' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                {/* Title */}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontWeight: 500,
                    color: isSelected ? 'primary.main' : COLORS.textSecondary,
                  }}
                >
                  {item.displayName}
                </Typography>

                {/* Count */}
                {typeof item.count === 'number' && (
                  <Typography variant="body2" sx={{ color: isSelected ? 'primary.main' : COLORS.textTertiary }}>
                    {item.count.toLocaleString()}
                  </Typography>
                )}

                {/* Checkbox */}
                <Box
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => handleItemCheckboxKeyDown(e, item.uid, item.disabled)}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={item.disabled}
                    onChange={() => onItemChange(item.uid)}
                    size="small"
                    slotProps={{
                      input: {
                        'aria-label': `Select ${item.displayName}`,
                      },
                    }}
                  />
                </Box>
              </ChildRow>
            );
          })}
        </ChildrenContainer>
      </Collapse>
    </Box>
  );
};
